/**
 * La résiliation d'une entreprise, vue de l'intérieur
 * (`20260301006000_tenant_termination`).
 *
 * Posée par le staff, à une date d'effet. Jusque-là, rien ne change ; à
 * partir d'elle, trente jours de lecture seule, puis la suppression. Le
 * client n'en voit que trois dates — jamais la note du staff.
 */

/** Ce que la base rend à un membre de l'entreprise. */
export interface TenantTermination {
  /** Premier jour de lecture seule, AAAA-MM-JJ, à Paris. */
  effectiveOn: string;
  /** Jour de la suppression, trente jours après. */
  purgeOn: string;
  readOnly: boolean;
}

export type TerminationPhase = "annoncee" | "lecture_seule";

export interface TerminationState {
  phase: TerminationPhase;
  effectiveOn: string;
  purgeOn: string;
  /** Jours avant la lecture seule ; 0 une fois commencée. */
  daysUntilEffect: number;
  /** Jours avant la suppression, jamais négatif. */
  daysUntilPurge: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Jours entiers entre deux dates AAAA-MM-JJ, sans fuseau pour les fausser. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/**
 * L'état à afficher. `readOnly` vient de la base, qui fait foi : c'est elle
 * qui refuse les écritures, et l'écran ne doit pas annoncer une lecture
 * seule qu'elle n'applique pas — ni l'inverse.
 */
export function terminationState(
  termination: TenantTermination | null,
  today: string,
): TerminationState | null {
  if (!termination) return null;
  return {
    phase: termination.readOnly ? "lecture_seule" : "annoncee",
    effectiveOn: termination.effectiveOn,
    purgeOn: termination.purgeOn,
    daysUntilEffect: termination.readOnly ? 0 : Math.max(0, daysBetween(today, termination.effectiveOn)),
    daysUntilPurge: Math.max(0, daysBetween(today, termination.purgeOn)),
  };
}

/** Le refus que lève la base sur une écriture en lecture seule. */
export const READ_ONLY_SQLSTATE = "DK001";
